import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { 
  Upload, FolderPlus, Download, Trash2, Search, File, 
  FileText, Image, FileArchive, Folder, Eye, MoreVertical, ArrowLeft, CloudUpload
} from 'lucide-react';

const FileManager = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCloudUploadDialog, setShowCloudUploadDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [cloudStorageConfigured, setCloudStorageConfigured] = useState(false);
  const [uploadingToCloud, setUploadingToCloud] = useState(false);

  useEffect(() => {
    loadFiles();
    checkCloudStorage();
  }, [currentFolder]);

  const checkCloudStorage = async () => {
    try {
      const response = await axiosInstance.get('/admin/cloud-storage');
      setCloudStorageConfigured(response.data?.configured || false);
    } catch (error) {
      console.error('Failed to check cloud storage:', error);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await axiosInstance.get('/file-manager/list', {
        params: { folderId: currentFolder }
      });
      setFiles(response.data.files || []);
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to load files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      await axiosInstance.post('/file-manager/folder', {
        name: newFolderName,
        parentId: currentFolder
      });
      toast.success('Folder created successfully');
      setNewFolderName('');
      setShowCreateFolderDialog(false);
      loadFiles();
    } catch (error) {
      console.error('Failed to create folder:', error);
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFiles(e.target.files);
  };

  const handleUploadFiles = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const formData = new FormData();
        formData.append('file', selectedFiles[i]);
        if (currentFolder) formData.append('folderId', currentFolder);

        await axiosInstance.post('/file-manager/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      }

      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles(null);
      setShowUploadDialog(false);
      loadFiles();
    } catch (error) {
      console.error('Failed to upload files:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUploadToCloud = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploadingToCloud(true);

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const formData = new FormData();
        formData.append('file', selectedFiles[i]);
        if (currentFolder) formData.append('folderId', currentFolder);

        await axiosInstance.post('/file-manager/upload-to-cloud', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success(`${selectedFiles.length} file(s) uploaded to cloud successfully`);
      setSelectedFiles(null);
      setShowCloudUploadDialog(false);
      loadFiles();
    } catch (error) {
      console.error('Failed to upload to cloud:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files to cloud');
    } finally {
      setUploadingToCloud(false);
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axiosInstance.get(`/file-manager/download/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/file-manager/file/${fileId}`);
      toast.success('File deleted successfully');
      loadFiles();
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Delete folder "${folderName}" and all its contents?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/file-manager/folder/${folderId}`);
      toast.success('Folder deleted successfully');
      loadFiles();
    } catch (error) {
      console.error('Failed to delete folder:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)) {
      return <Image className="w-5 h-5 text-blue-600" />;
    } else if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="w-5 h-5 text-red-600" />;
    } else if (['zip', 'rar', 'tar', 'gz'].includes(ext)) {
      return <FileArchive className="w-5 h-5 text-yellow-600" />;
    }
    return <File className="w-5 h-5 text-gray-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* SafetyMind AI Branding */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">SM</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">SafetyMind AI</h2>
              <p className="text-xs text-gray-500 italic">Safety That Thinks</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">File Manager</h1>
          <p className="text-gray-600 mt-1">Upload, organize, and manage company files</p>
        </div>

        {/* Action Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <Button onClick={() => setShowUploadDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                <Upload className="w-4 h-4 mr-2" />
                Upload Files
              </Button>
              {cloudStorageConfigured && (
                <Button onClick={() => setShowCloudUploadDialog(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload to Cloud
                </Button>
              )}
              <Button onClick={() => setShowCreateFolderDialog(true)} variant="outline">
                <FolderPlus className="w-4 h-4 mr-2" />
                New Folder
              </Button>
              <div className="flex-1 max-w-md relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search files and folders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Breadcrumb */}
        {currentFolder && (
          <div className="mb-4">
            <Button variant="link" onClick={() => setCurrentFolder(null)} className="text-blue-600">
              ← Back to root
            </Button>
          </div>
        )}

        {/* Files and Folders */}
        <Card>
          <CardHeader>
            <CardTitle>
              {currentFolder ? 'Current Folder' : 'All Files & Folders'}
            </CardTitle>
            <CardDescription>
              {filteredFolders.length} folder(s), {filteredFiles.length} file(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredFolders.length === 0 && filteredFiles.length === 0 && currentFolder !== null ? (
              <div className="text-center py-12 text-gray-500">
                <Folder className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No files or folders</p>
                <p className="text-sm">Upload files or create folders to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Modified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Special Folder: New Hire Documents */}
                  {!currentFolder && (
                    <TableRow 
                      className="cursor-pointer hover:bg-blue-50 bg-blue-25"
                      onClick={() => navigate('/admin/new-hires')}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <span className="text-blue-700 font-semibold">📄 New Hire Documents</span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Worker Forms</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500">-</TableCell>
                      <TableCell className="text-gray-500">System Folder</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/admin/new-hires');
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Special Folder: SDS Library */}
                  {!currentFolder && (
                    <TableRow 
                      className="cursor-pointer hover:bg-cyan-50 bg-cyan-25"
                      onClick={() => navigate('/admin/sds-library')}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-cyan-600" />
                          <span className="text-cyan-700 font-semibold">🧪 SDS Library</span>
                          <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded">Safety Sheets</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500">-</TableCell>
                      <TableCell className="text-gray-500">System Folder</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-cyan-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/admin/sds-library');
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                  
                  {/* Regular Folders */}
                  {filteredFolders.map((folder) => (
                    <TableRow 
                      key={folder.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setCurrentFolder(folder.id)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Folder className="w-5 h-5 text-yellow-600" />
                          {folder.name}
                        </div>
                      </TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>{new Date(folder.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id, folder.name);
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Files */}
                  {filteredFiles.map((file) => (
                    <TableRow key={file.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {getFileIcon(file.name)}
                          {file.name}
                        </div>
                      </TableCell>
                      <TableCell>{formatFileSize(file.size)}</TableCell>
                      <TableCell>{new Date(file.uploadedAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadFile(file.id, file.name)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteFile(file.id, file.name)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolderDialog} onOpenChange={setShowCreateFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>Enter a name for the new folder</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateFolderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Files Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
            <DialogDescription>Select one or more files to upload</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
            {selectedFiles && (
              <p className="text-sm text-gray-600 mt-2">
                {selectedFiles.length} file(s) selected
              </p>
            )}
            {uploading && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUploadFiles} disabled={uploading || !selectedFiles}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload to Cloud Dialog */}
      <Dialog open={showCloudUploadDialog} onOpenChange={setShowCloudUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files to Cloud Storage</DialogTitle>
            <DialogDescription>Upload files directly to your configured cloud storage (Cloudflare R2)</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploadingToCloud}
            />
            {selectedFiles && (
              <p className="text-sm text-gray-600 mt-2">
                {selectedFiles.length} file(s) selected for cloud upload
              </p>
            )}
            {uploadingToCloud && (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-sm text-purple-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span>Uploading to cloud storage...</span>
                </div>
              </div>
            )}
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-xs text-purple-800">
                <strong>Note:</strong> Files uploaded to cloud storage will be stored in your Cloudflare R2 bucket for persistent, scalable storage.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloudUploadDialog(false)} disabled={uploadingToCloud}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadToCloud} 
              disabled={uploadingToCloud || !selectedFiles}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploadingToCloud ? 'Uploading...' : 'Upload to Cloud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileManager;
